import { mseIsSupported, NEXT_VIDEO_THRESHOLD, Media } from "./video";
import SourceBufferManager from "./SourceBufferManager";
import VideoQueue from "./VideoQueue";
const LOGS = { VIDEO: false };

export default class MediaSourcePlayer {
    private _videoElement: HTMLVideoElement;
    private _imageElement: HTMLImageElement;
    private _videoQueue: VideoQueue;
    private _sourceBuffer: SourceBufferManager | null = null;
    private _mediaSource: MediaSource | null = null;
    private _navigate: any;
    private mediaType: "video" | "image" = "video";

    constructor(videoElement: HTMLVideoElement, imageElement: HTMLImageElement, navigate: any) {
        this._navigate = navigate;
        this._videoElement = videoElement;
        this._imageElement = imageElement;
        this._videoQueue = new VideoQueue();

        if (mseIsSupported()) {
            this.initMSE();
        } else {
            console.warn("MSE is not supported. Falling back to basic playback.");
        }
    }

    private get secondsUntilVideoEnd() {
        return this.totalVideoDuration - this._videoElement.currentTime;
    }

    private get totalVideoDuration() {
        return this._videoElement.duration;
    }

    private initMSE = () => {
        this._mediaSource = new MediaSource();
        this._sourceBuffer = new SourceBufferManager(this._videoElement, this._mediaSource);
        this._videoQueue = new VideoQueue();

        const onSourceOpen = () => {
            this._sourceBuffer!.initSourceBuffer();
            this._mediaSource!.removeEventListener("sourceopen", onSourceOpen);
        };

        const maybeLoadNextVideo = () => {
            if (this.mediaType !== "video") return;
            LOGS.VIDEO &&
                console.log(
                    "Total video duration: ",
                    this.totalVideoDuration,
                    "\nSeconds until video end: ",
                    this.secondsUntilVideoEnd,
                );
            if (this.secondsUntilVideoEnd <= NEXT_VIDEO_THRESHOLD) {
                const currentVideo = this._videoQueue.head();
                if (currentVideo.navigateAfter) {
                    this._navigate(currentVideo.navigateAfter, {
                        replace: true,
                    });
                }
                LOGS.VIDEO && console.log("adding next video segment");
                const nextVideo = this._videoQueue.getNextVideo();
                this.show(nextVideo);
            }
        };

        this._mediaSource.addEventListener("sourceopen", onSourceOpen);
        const url = URL.createObjectURL(this._mediaSource);
        this._videoElement.src = url;
        this._videoElement.addEventListener("timeupdate", maybeLoadNextVideo);
    };

    // private hideVideo = () => (this._videoElement.style.display = "none");
    private showVideo = () => (this._videoElement.style.display = "");
    private hideImage = () => (this._imageElement.style.display = "none");
    private showImage = () => (this._imageElement.style.display = "");

    private enableVideoDisplay() {
        this.showVideo();
        this.hideImage();
        this.mediaType = "video";
    }

    private enableImageDisplay() {
        this.showImage();
        this.mediaType = "image";
        // this.hideVideo();
    }

    show = (mediaItem: Media) => {
        this._videoQueue.clear();

        const isVideo = mediaItem.contentType.includes("video");

        if (!isVideo) {
            LOGS.VIDEO &&
                console.log("Received image element, showing as an image and hiding the video.");
            // TODO - optimization. We should be tracking and revoking these object URLs: https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL_static#memory_management
            const blobUrl = URL.createObjectURL(
                new Blob([mediaItem.data], { type: mediaItem.contentType }),
            );
            this._imageElement.src = blobUrl;
            this.enableImageDisplay();
            return;
        } else {
            this.enableVideoDisplay();
        }

        this._videoQueue.enqueue(mediaItem);

        if (!mseIsSupported()) {
            const blobUrl = URL.createObjectURL(
                new Blob([mediaItem.data], { type: mediaItem.contentType }),
            );
            this._videoElement.src = blobUrl;
            this._videoElement.play().catch(console.error);
            return;
        }
        this._sourceBuffer!.playVideo(mediaItem);
    };
}
